if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this.loadVersion = 0;
    }

    hide(preventFocus = false) {
      this.loadVersion++;
      this.loadAbort?.abort();
      this.loading = false;
      this.setOpenerLoading(false);
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      const drawer = document.querySelector('cart-drawer');
      const restoreDrawer = drawer?.classList.contains('active') && drawer.trapped && window.OwnStore;
      // The cart opens before ProductForm closes quick-add. Temporarily remove
      // that top trap, then let the base dialog remove its own trap below it.
      if (restoreDrawer) { window.OwnStore.removeTrapFocus(); drawer.trapped = null; }
      this.modalContent.innerHTML = '';
      this.modalContent.removeAttribute('aria-busy');

      if (preventFocus) this.openedBy = null;
      super.hide();
      if (restoreDrawer) drawer.open();
    }

    setOpenerLoading(loading) {
      const opener = this.loadingOpener;
      if (!opener) return;
      opener.disabled = loading || this.openerWasDisabled;
      opener.toggleAttribute('aria-disabled', loading || this.openerWasDisabled);
      opener.classList.toggle('loading', loading);
      opener.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !loading);
    }

    show(opener) {
      if (this.loading) return;
      this.loading = true;
      const version = ++this.loadVersion;
      this.loadAbort = new AbortController();
      this.loadingOpener = opener; this.openerWasDisabled = opener.disabled;
      this.setOpenerLoading(true);
      this.modalContent.setAttribute('aria-busy', 'true');
      const status = document.createElement('p');
      status.className = 'quick-add-modal__loading'; status.setAttribute('role', 'status');
      status.textContent = this.dataset.loadingLabel || window.OwnStore?.config.strings?.loading || 'Loading...';
      this.modalContent.replaceChildren(status);
      super.show(opener);

      return fetch(opener.getAttribute('data-product-url'), { signal: this.loadAbort.signal, credentials: 'same-origin' })
        .then((response) => { if (!response.ok) throw new Error('Product options could not be loaded. Please try again or open the product page.'); return response.text(); })
        .then((responseText) => {
          if (version !== this.loadVersion) return;
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          if (!this.productElement) throw new Error('Quick view could not load this product. Open the product page to continue.');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          const mediaGallery = this.productElement.querySelector('media-gallery');
          // if (mediaGallery) {
          //   mediaGallery.dataset.disablePrepend = 'false';
          //   mediaGallery.removeAttribute('data-filtering-option');
          //   mediaGallery.querySelectorAll('.product__media-item.hidden, .thumbnail-list__item.hidden').forEach(item => {
          //     item.classList.remove('hidden');
          //   })
          // }
          // if (this.productElement.querySelector('variant-selects')) this.productElement.querySelector('variant-selects').dataset.hasFiltering = 'false';
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
        })
        .catch(error => {
          if (error.name === 'AbortError' || version !== this.loadVersion) return;
          window.OwnStore?.report(error, 'quick-add');
          const message = document.createElement('p'); message.className = 'ownstore-error'; message.setAttribute('role', 'alert'); message.textContent = window.OwnStore?.config.strings?.product_form_not_ready || error.message;
          const link = document.createElement('a'); link.className = 'button button--secondary'; link.href = opener.getAttribute('data-product-url');
          link.textContent = this.dataset.viewProductLabel || window.OwnStore?.config.strings?.viewProduct || 'View full details';
          this.modalContent.replaceChildren(message, link);
        })
        .finally(() => {
          if (version !== this.loadVersion) return;
          this.loading = false; this.setOpenerLoading(false);
          this.modalContent.setAttribute('aria-busy', 'false');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        if (oldScriptTag.src) {
          const src = oldScriptTag.src;
          const exists = [...document.scripts].some(script => script !== oldScriptTag && script.src === src);
          if (!exists) {
            const external = document.createElement('script');
            [...oldScriptTag.attributes].forEach(attribute => external.setAttribute(attribute.name, attribute.value));
            document.head.append(external);
          }
          oldScriptTag.remove();
          return;
        }
        if (oldScriptTag.type && !/^(?:text|application)\/javascript$/.test(oldScriptTag.type)) return;
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      this.modalContent.querySelectorAll('variant-radios,variant-selects,product-info').forEach(picker => picker.setAttribute('data-update-url', 'false'));
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const stickyAtc = this.productElement.querySelector('sticky-atc');
      if (stickyAtc) stickyAtc.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      if (!sectionId) throw new Error('Product options are incomplete. Open the product page to continue.');
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${this.id}-${sectionId}`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
        element.dataset.updateUrl = 'false';
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product?.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
